import React, { useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import useForm from 'react-hook-form';
import Select from 'react-select';
import PropTypes from 'prop-types';
import { Switch } from 'pretty-checkbox-react';
/* actions */
import { addProducts } from '../../redux/actions/products';
import { getAllManufacturers } from '../../redux/actions/manufacturers';
import { getAllSuppliers } from '../../redux/actions/suppliers';
import { getAllCategories } from '../../redux/actions/categories';
/* components */
import ErrorMsgField from '../../components/ErrorMsgField';
import AlertMsg from '../../components/AlertMsg';
import FormFooter from '../../components/FormFooter';
import FormHeader from '../../components/FormHeader';
import MultiSelect from '../../components/MultiSelect';
import ModalAddManufacturer from '../../components/ModalAddManufacturer';
import ModalAddSupplier from '../../components/ModalAddSupplier';
import ModalAddCategory from '../../components/ModalAddCategory';
/* form-validation */
import schema from '../../form-validation/product';
/* utils */
import {
  customStyle,
  customThemes,
  customFilter,
  firstOption,
  handleChangeSelect
} from '../../utils/select';
import sortable from '../../utils/sortable';
import {
  handleMultiSelect,
  handleMultiDeselect
} from '../../utils/multiSelect';
import handleChangeDefault from '../../utils/handleChangeDefault';
import handleChangeCurrency from '../../utils/handleChangeCurrency';
import origins from '../../utils/origins';
import itensTypes from '../../utils/itensTypes';
import calcProfitPercentage from '../../utils/calcProfitPercentage';
import config from '../../api/config.json';

function Add (props) {
  const { handleModalClose } = props;
  const dispatch = useDispatch();
  const submitButton = useRef();
  const { register, handleSubmit, errors, reset, setError, setValue } = useForm(
    {
      validationSchema: schema,
      reValidateMode: 'onSubmit'
    }
  );
  const sessionUser = useSelector(state => state.authentications.user);
  const company = sessionUser.company._id;
  const INITIAL_VALUES = {
    ean: '',
    model: '',
    manufacturer: '',
    status: true,
    enabledDropshipping: true,
    name: '',
    sku: '',
    supplier: '',
    minDropshippingQuantity: 10,
    purchasePrice: '0,00',
    retailSaleValue: '0,00',
    wholeSaleValue: '0,00',
    stockQuantity: 0,
    dropshippingValue: '0,00',
    wholesaleMinQuantity: '',
    weight: '0,00',
    length: '0,00',
    width: '0,00',
    height: '0,00',
    origin: '0',
    itemType: '00',
    condition: 'NEW',
    ncm: '',
    cest: '',
    csosn: '',
    cfopSameState: '',
    cfopAnotherState: '',
    location: '',
    link: '',
    additionalInformation: '',
    image: '',
    categories: [],
    company,
    createdByUser: sessionUser._id
  };
  const products = useSelector(state => state.products);
  const manufacturers = useSelector(state => state.manufacturers.data);
  const suppliers = useSelector(state => state.suppliers.data);
  const categories = useSelector(state => state.categories.data);
  const [data, setData] = useState(INITIAL_VALUES);
  const [manufacturerOptions, setManufacturerOptions] = useState([firstOption]);
  const [supplierOptions, setSupplierOptions] = useState([firstOption]);
  const [categoriesOptions, setCategoriesOptions] = useState({
    categories: []
  });
  const [ctgrs, setCtgrs] = useState([]);
  const [showModalAddManufacturer, setShowModalAddManufacturer] = useState(
    false
  );
  const [showModalAddSupplier, setShowModalAddSupplier] = useState(false);
  const [showModalAddCategory, setShowModalAddCategory] = useState(false);

  useEffect(() => {
    dispatch(getAllManufacturers({ company }));
    dispatch(getAllSuppliers({ company }));
    dispatch(getAllCategories({ company }));
  }, [dispatch]);

  useEffect(() => {
    if (categories.length) {
      const ctgrs = categories
        .map(category => ({
          sortValue: category.name,
          value: category._id,
          text: category.name
        }))
        .sort(sortable);
      setCtgrs([...ctgrs]);
    }
  }, [categories, setCtgrs]);

  useEffect(() => {
    if (manufacturers.length) {
      const options = manufacturers
        .map(manufacturer => ({
          value: manufacturer._id,
          label: `${manufacturer.name}`,
          sortValue: `${manufacturer.name}`
        }))
        .sort(sortable);

      options.unshift(firstOption);
      setManufacturerOptions([...options]);
    }
  }, [manufacturers, setManufacturerOptions]);

  useEffect(() => {
    if (suppliers.length) {
      const options = suppliers
        .map(supplier => ({
          value: supplier._id,
          label: `${supplier.name}`,
          sortValue: `${supplier.name}`
        }))
        .sort(sortable);

      options.unshift(firstOption);
      setSupplierOptions([...options]);
    }
  }, [suppliers, setSupplierOptions]);

  const submit = async () => {
    const resetForm = () => {
      reset();
      setData({
        ...INITIAL_VALUES
      });
      Object.keys(categoriesOptions).forEach(key =>
        setCategoriesOptions({ ...categoriesOptions, [key]: [] })
      );
    };
    const newData = {
      ...data,
      purchasePrice: parseFloat(data.purchasePrice.replace(/,/g, '.')),
      retailSaleValue: parseFloat(data.retailSaleValue.replace(/,/g, '.')),
      wholeSaleValue: parseFloat(data.wholeSaleValue.replace(/,/g, '.')),
      dropshippingValue: parseFloat(data.dropshippingValue.replace(/,/g, '.')),
      weight: parseFloat(data.weight.replace(/,/g, '.')),
      length: parseFloat(data.length.replace(/,/g, '.')),
      width: parseFloat(data.width.replace(/,/g, '.')),
      height: parseFloat(data.height.replace(/,/g, '.'))
    };
    dispatch(addProducts(newData, resetForm, submitButton.current));
  };

  const handleChangeImage = evt => {
    const { files } = evt.target;
    const suportedTypes = ['image/png', 'image/jpeg'];
    files[0] && suportedTypes.includes(files[0].type)
      ? setData({ ...data, image: files[0] })
      : setData({ ...data, image: '' });
  };

  return (
    <div
      className="container-fluid"
      style={{
        display: `${
          (showModalAddCategory || showModalAddManufacturer) && handleModalClose
            ? 'none'
            : 'block'
        }`
      }}
    >
      <ModalAddManufacturer
        showModal={showModalAddManufacturer}
        handleModalClose={() => setShowModalAddManufacturer(false)}
      />
      <ModalAddSupplier
        showModal={showModalAddSupplier}
        handleModalClose={() => setShowModalAddSupplier(false)}
      />
      <ModalAddCategory
        showModal={showModalAddCategory}
        handleModalClose={() => setShowModalAddCategory(false)}
      />
      {products.addSuccess && (
        <AlertMsg type="success" msg="Produto cadastrado com sucesso" />
      )}
      {products.errorContent &&
        !products.errorContent.message.includes('400') &&
        products.addError && (
        <AlertMsg type="danger" msg="Falha durante cadastro do produto" />
      )}
      {products.errorContent &&
        products.errorContent.message.includes('400') &&
        products.addError && (
        <AlertMsg
          type="danger"
          msg="Um produto com o SKU informado já está cadastrado"
        />
      )}
      {!products.addSuccess && !products.addError && (
        <AlertMsg type="info" msg="Área cadastro de produtos" />
      )}
      <FormHeader title="Cadastrar Produto" />
      <form onSubmit={handleSubmit(submit)}>
        <div className="row">
          <div className="col-sm-2">
            <div className="form-group">
              <label htmlFor="ean">
                EAN (GTIN)<span className="field-required">*</span>
              </label>
              <input
                type="text"
                className="form-control"
                id="ean"
                name="ean"
                autoComplete="off"
                value={data.ean}
                onChange={evt => handleChangeDefault(evt, data, setData)}
                ref={register}
              />
              {errors.ean && errors.ean.type === 'required' && (
                <ErrorMsgField type="required" />
              )}
            </div>
          </div>
          <div className="col-sm-2">
            <div className="form-group">
              <label htmlFor="model">
                Modelo<span className="field-required">*</span>
              </label>
              <input
                type="text"
                className="form-control"
                id="model"
                name="model"
                autoComplete="off"
                value={data.model}
                onChange={evt => handleChangeDefault(evt, data, setData)}
                ref={register}
              />
              {errors.model && errors.model.type === 'required' && (
                <ErrorMsgField type="required" />
              )}
            </div>
          </div>
          <div className="col-sm-4">
            <div className="form-group">
              <div className="form-group form-inline mb-0">
                <label
                  className="col-sm-12 d-inline p-0 mb-2"
                  htmlFor="manufacturer"
                >
                  Fabricante<span className="field-required">*</span>
                </label>
                <Select
                  className="col p-0"
                  name="manufacturer"
                  ref={register}
                  onChange={evt =>
                    handleChangeSelect(
                      { ...evt, name: 'manufacturer' },
                      data,
                      setData,
                      setError,
                      setValue
                    )
                  }
                  defaultValue={manufacturerOptions[0]}
                  value={manufacturerOptions.find(
                    manufacturer => manufacturer.value === data.manufacturer
                  )}
                  options={manufacturerOptions}
                  isSearchable={true}
                  theme={customThemes}
                  styles={customStyle}
                  filterOption={customFilter}
                  noOptionsMessage={() => 'Nenhum registro encontrado'}
                />
                <div className="pl-2">
                  <button
                    type="button"
                    className="btn btn-success"
                    title="Adicionar Fabricante"
                    disabled={showModalAddManufacturer}
                    onClick={() => setShowModalAddManufacturer(true)}
                  >
                    <i className="fa fa-plus" />
                    &nbsp;Fabricante
                  </button>
                </div>
              </div>
              {errors.manufacturer &&
                errors.manufacturer.type === 'required' && (
                <ErrorMsgField type="required" />
              )}
            </div>
          </div>
          <div className="col-sm-2">
            <div className="form-group">
              <label htmlFor="status">Status</label>
              <div className="checkbox">
                <Switch
                  type="checkbox"
                  color="success"
                  name="status"
                  onChange={evt => {
                    setData({
                      ...data,
                      [evt.target.name]: evt.target.checked
                    });
                  }}
                  checked={data.status}
                >
                  {data.status ? 'Ativado' : 'Desativado'}
                </Switch>
              </div>
            </div>
          </div>
          <div className="col-sm-2">
            <div className="form-group">
              <label htmlFor="enabledDropshipping">DropShipping</label>
              <div className="checkbox">
                <Switch
                  type="checkbox"
                  color="success"
                  name="enabledDropshipping"
                  onChange={evt => {
                    setData({
                      ...data,
                      [evt.target.name]: evt.target.checked
                    });
                  }}
                  checked={data.enabledDropshipping}
                >
                  {data.enabledDropshipping ? 'Ativado' : 'Desativado'}
                </Switch>
              </div>
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-sm-3">
            <div className="form-group">
              <label htmlFor="name">
                Nome<span className="field-required">*</span>
              </label>
              <input
                type="text"
                className="form-control"
                id="name"
                name="name"
                autoComplete="off"
                value={data.name}
                onChange={evt => handleChangeDefault(evt, data, setData)}
                ref={register}
              />
              {errors.name && errors.name.type === 'required' && (
                <ErrorMsgField type="required" />
              )}
            </div>
          </div>
          <div className="col-sm-2">
            <div className="form-group">
              <label htmlFor="name">
                SKU<span className="field-required">*</span>
              </label>
              <input
                type="text"
                className="form-control"
                id="sku"
                name="sku"
                autoComplete="off"
                value={data.sku}
                onChange={evt => handleChangeDefault(evt, data, setData)}
                ref={register}
              />
              {errors.sku && errors.sku.type === 'required' && (
                <ErrorMsgField type="required" />
              )}
            </div>
          </div>
          <div className="col-sm-4">
            <div className="form-group">
              <div className="form-group form-inline mb-0">
                <label
                  className="col-sm-12 d-inline p-0 mb-2"
                  htmlFor="supplier"
                >
                  Fornecedor<span className="field-required">*</span>
                </label>
                <Select
                  className="col p-0"
                  name="supplier"
                  ref={register}
                  onChange={evt =>
                    handleChangeSelect(
                      { ...evt, name: 'supplier' },
                      data,
                      setData,
                      setError,
                      setValue
                    )
                  }
                  defaultValue={supplierOptions[0]}
                  value={supplierOptions.find(
                    supplier => supplier.value === data.supplier
                  )}
                  options={supplierOptions}
                  isSearchable={true}
                  theme={customThemes}
                  styles={customStyle}
                  filterOption={customFilter}
                  noOptionsMessage={() => 'Nenhum registro encontrado'}
                />
                <div className="pl-2">
                  <button
                    type="button"
                    className="btn btn-success"
                    title="Adicionar Fornecedor"
                    disabled={showModalAddSupplier}
                    onClick={() => setShowModalAddSupplier(true)}
                  >
                    <i className="fa fa-plus" />
                    &nbsp;Fornecedor
                  </button>
                </div>
              </div>
              {errors.supplier && errors.supplier.type === 'required' && (
                <ErrorMsgField type="required" />
              )}
            </div>
          </div>
          <div className="col-sm-3">
            <div className="form-group">
              <label htmlFor="minDropshippingQuantity">
                Quantidade Mín. DropShipping
                {/* <span className="field-required">*</span> */}
              </label>
              <input
                type="text"
                className="form-control"
                id="minDropshippingQuantity"
                name="minDropshippingQuantity"
                autoComplete="off"
                number="on"
                value={data.minDropshippingQuantity}
                onChange={evt => handleChangeDefault(evt, data, setData)}
                ref={register}
              />
              {errors.minDropshippingQuantity &&
                errors.minDropshippingQuantity.type === 'required' && (
                <ErrorMsgField type="required" />
              )}
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-sm-3">
            <div className="form-group">
              <label htmlFor="purchasePrice">
                R$ Valor de Compra<span className="field-required">*</span>
              </label>
              <input
                type="text"
                className="form-control"
                id="purchasePrice"
                name="purchasePrice"
                number="on"
                value={data.purchasePrice}
                autoComplete="off"
                onChange={evt => handleChangeCurrency(evt, data, setData)}
                ref={register}
              />
              {errors.purchasePrice &&
                errors.purchasePrice.type === 'currency' && (
                <ErrorMsgField type="currency" />
              )}
              {errors.purchasePrice &&
                errors.purchasePrice.type === 'required' && (
                <ErrorMsgField type="required" />
              )}
              {errors.purchasePrice && errors.purchasePrice.type === 'min' && (
                <ErrorMsgField type="min" />
              )}
            </div>
          </div>
          <div className="col-sm-3">
            <div className="form-group">
              <label htmlFor="retailSaleValue">
                R$ Valor de Varejo
                {/* <span className="field-required">*</span> */}
              </label>
              <input
                type="text"
                className="form-control"
                id="retailSaleValue"
                name="retailSaleValue"
                value={data.retailSaleValue}
                autoComplete="off"
                onChange={evt => handleChangeCurrency(evt, data, setData)}
                ref={register}
              />
              {errors.retailSaleValue &&
                errors.retailSaleValue.type === 'currency' && (
                <ErrorMsgField type="currency" />
              )}
              {errors.retailSaleValue &&
                errors.retailSaleValue.type === 'required' && (
                <ErrorMsgField type="required" />
              )}
              {errors.retailSaleValue &&
                errors.retailSaleValue.type === 'min' && (
                <ErrorMsgField type="min" />
              )}
            </div>
          </div>
          <div className="col-sm-3">
            <div className="form-group">
              <label htmlFor="wholeSaleValue">
                R$ Valor de Atacado
                {/* <span className="field-required">*</span> */}
              </label>
              <input
                type="text"
                className="form-control"
                id="wholeSaleValue"
                name="wholeSaleValue"
                value={data.wholeSaleValue}
                autoComplete="off"
                onChange={evt => handleChangeCurrency(evt, data, setData)}
                ref={register}
              />
              {errors.wholeSaleValue &&
                errors.wholeSaleValue.type === 'currency' && (
                <ErrorMsgField type="currency" />
              )}
              {errors.wholeSaleValue &&
                errors.wholeSaleValue.type === 'required' && (
                <ErrorMsgField type="required" />
              )}
              {errors.wholeSaleValue &&
                errors.wholeSaleValue.type === 'min' && (
                <ErrorMsgField type="min" />
              )}
            </div>
          </div>
          <div className="col-sm-3">
            <div className="form-group">
              <label htmlFor="stockQuantity">
                Quantidade Estoque
                {/* <span className="field-required">*</span> */}
              </label>
              <input
                type="text"
                className="form-control"
                id="stockQuantity"
                name="stockQuantity"
                autoComplete="off"
                number="on"
                value={data.stockQuantity}
                onChange={evt => handleChangeDefault(evt, data, setData)}
                ref={register}
              />
              {errors.stockQuantity &&
                errors.stockQuantity.type === 'required' && (
                <ErrorMsgField type="required" />
              )}
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-sm-3">
            <div className="form-group">
              <label htmlFor="dropshippingValue">
                R$ V. de DropShipping | Margem:{' '}
                {calcProfitPercentage(
                  data.purchasePrice,
                  data.dropshippingValue
                )}
                %{/* <span className="field-required">*</span> */}
              </label>
              <input
                type="text"
                className="form-control"
                id="dropshippingValue"
                name="dropshippingValue"
                value={data.dropshippingValue}
                autoComplete="off"
                onChange={evt => handleChangeCurrency(evt, data, setData)}
                ref={register}
              />
              {errors.dropshippingValue &&
                errors.dropshippingValue.type === 'currency' && (
                <ErrorMsgField type="currency" />
              )}
              {errors.dropshippingValue &&
                errors.dropshippingValue.type === 'required' && (
                <ErrorMsgField type="required" />
              )}
              {errors.dropshippingValue &&
                errors.dropshippingValue.type === 'min' && (
                <ErrorMsgField type="min" />
              )}
            </div>
          </div>
          <div className="col-sm-3">
            <div className="form-group">
              <label htmlFor="profitRetailSaleValue">
                R$ Valor de Lucro Varejo | Margem:{' '}
                {calcProfitPercentage(data.purchasePrice, data.retailSaleValue)}
                %{/* <span className="field-required">*</span> */}
              </label>
              <input
                type="text"
                className="form-control"
                id="profitRetailSaleValue"
                name="profitRetailSaleValue"
                value={
                  !isNaN(
                    parseFloat(data.retailSaleValue.replace(/,/g, '.')) -
                      parseFloat(data.purchasePrice.replace(/,/g, '.'))
                  )
                    ? (
                      parseFloat(data.retailSaleValue.replace(/,/g, '.')) -
                        parseFloat(data.purchasePrice.replace(/,/g, '.'))
                    )
                      .toFixed(2)
                      .replace(/\./g, ',')
                    : '0,00'
                }
                disabled={true}
                autoComplete="off"
                ref={register}
              />
              {errors.profitRetailSaleValue &&
                errors.profitRetailSaleValue.type === 'required' && (
                <ErrorMsgField type="required" />
              )}
            </div>
          </div>
          <div className="col-sm-3">
            <div className="form-group">
              <label htmlFor="profitWholesaleSaleValue">
                R$ Valor de Lucro Atacado | Margem:{' '}
                {calcProfitPercentage(data.purchasePrice, data.wholeSaleValue)}%
                {/* <span className="field-required">*</span> */}
              </label>
              <input
                type="text"
                className="form-control"
                id="profitWholesaleSaleValue"
                name="profitWholesaleSaleValue"
                value={
                  !isNaN(
                    parseFloat(data.wholeSaleValue.replace(/,/g, '.')) -
                      parseFloat(data.purchasePrice.replace(/,/g, '.'))
                  )
                    ? (
                      parseFloat(data.wholeSaleValue.replace(/,/g, '.')) -
                        parseFloat(data.purchasePrice.replace(/,/g, '.'))
                    )
                      .toFixed(2)
                      .replace(/\./g, ',')
                    : '0,00'
                }
                disabled={true}
                autoComplete="off"
                ref={register}
              />
              {errors.profitWholesaleSaleValue &&
                errors.profitWholesaleSaleValue.type === 'required' && (
                <ErrorMsgField type="required" />
              )}
            </div>
          </div>
          <div className="col-sm-3">
            <div className="form-group">
              <label htmlFor="wholesaleMinQuantity">
                Quantidade Mín. de Peças Atacado
                {/* <span className="field-required">*</span> */}
              </label>
              <input
                type="text"
                className="form-control"
                id="wholesaleMinQuantity"
                name="wholesaleMinQuantity"
                autoComplete="off"
                number="on"
                value={data.wholesaleMinQuantity}
                onChange={evt => handleChangeDefault(evt, data, setData)}
                ref={register}
              />
              {errors.wholesaleMinQuantity &&
                errors.wholesaleMinQuantity.type === 'required' && (
                <ErrorMsgField type="required" />
              )}
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-sm-3">
            <div className="form-group">
              <label htmlFor="weight">
                Peso (Kg)
                {/* <span className="field-required">*</span> */}
              </label>
              <input
                type="text"
                className="form-control"
                id="weight"
                name="weight"
                number="on"
                value={data.weight}
                autoComplete="off"
                onChange={evt => handleChangeCurrency(evt, data, setData)}
                ref={register}
              />
              {errors.weight && errors.weight.type === 'currency' && (
                <ErrorMsgField type="currency" />
              )}
              {errors.weight && errors.weight.type === 'required' && (
                <ErrorMsgField type="required" />
              )}
              {errors.weight && errors.weight.type === 'min' && (
                <ErrorMsgField type="min" />
              )}
              {errors.weight && errors.weight.type === 'customRequired' && (
                <ErrorMsgField
                  type="customRequired"
                  msg="Formato obrigatório 0,00"
                />
              )}
            </div>
          </div>
          <div className="col-sm-3">
            <div className="form-group">
              <label htmlFor="length">
                Comprimento (cm)
                {/* <span className="field-required">*</span> */}
              </label>
              <input
                type="text"
                className="form-control"
                id="length"
                name="length"
                value={data.length}
                autoComplete="off"
                onChange={evt => handleChangeCurrency(evt, data, setData)}
                ref={register}
              />
              {errors.length && errors.length.type === 'currency' && (
                <ErrorMsgField type="currency" />
              )}
              {errors.length && errors.length.type === 'required' && (
                <ErrorMsgField type="required" />
              )}
              {errors.length && errors.length.type === 'min' && (
                <ErrorMsgField type="min" />
              )}
              {errors.length && errors.length.type === 'customRequired' && (
                <ErrorMsgField
                  type="customRequired"
                  msg="Formato obrigatório 0,00"
                />
              )}
            </div>
          </div>
          <div className="col-sm-3">
            <div className="form-group">
              <label htmlFor="width">
                Largura (cm)
                {/* <span className="field-required">*</span> */}
              </label>
              <input
                type="text"
                className="form-control"
                id="width"
                name="width"
                value={data.width}
                autoComplete="off"
                onChange={evt => handleChangeCurrency(evt, data, setData)}
                ref={register}
              />
              {errors.width && errors.width.type === 'currency' && (
                <ErrorMsgField type="currency" />
              )}
              {errors.width && errors.width.type === 'required' && (
                <ErrorMsgField type="required" />
              )}
              {errors.width && errors.width.type === 'min' && (
                <ErrorMsgField type="min" />
              )}
              {errors.width && errors.width.type === 'customRequired' && (
                <ErrorMsgField
                  type="customRequired"
                  msg="Formato obrigatório 0,00"
                />
              )}
            </div>
          </div>
          <div className="col-sm-3">
            <div className="form-group">
              <label htmlFor="height">
                Altura (cm)
                {/* <span className="field-required">*</span> */}
              </label>
              <input
                type="text"
                className="form-control"
                id="height"
                name="height"
                value={data.height}
                autoComplete="off"
                onChange={evt => handleChangeCurrency(evt, data, setData)}
                ref={register}
              />
              {errors.height && errors.height.type === 'currency' && (
                <ErrorMsgField type="currency" />
              )}
              {errors.height && errors.height.type === 'required' && (
                <ErrorMsgField type="required" />
              )}
              {errors.height && errors.height.type === 'min' && (
                <ErrorMsgField type="min" />
              )}
              {errors.height && errors.height.type === 'customRequired' && (
                <ErrorMsgField
                  type="customRequired"
                  msg="Formato obrigatório 0,00"
                />
              )}
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-sm-3">
            <div className="form-group">
              <label htmlFor="origin">
                Origem
                {/* <span className="field-required">*</span> */}
              </label>
              <select
                className="form-control"
                id="origin"
                name="origin"
                value={data.origin}
                onChange={evt => handleChangeDefault(evt, data, setData)}
                ref={register}
              >
                {Object.keys(origins).map((key, i) => {
                  return (
                    <option key={i} value={key}>
                      {key} - {origins[key]}
                    </option>
                  );
                })}
              </select>
              {errors.origin && errors.origin.type === 'required' && (
                <ErrorMsgField type="required" />
              )}
            </div>
          </div>
          <div className="col-sm-3">
            <div className="form-group">
              <label htmlFor="itemType">
                Tipo de Item
                {/* <span className="field-required">*</span> */}
              </label>
              <select
                className="form-control"
                id="itemType"
                name="itemType"
                value={data.itemType}
                onChange={evt => handleChangeDefault(evt, data, setData)}
                ref={register}
              >
                {Object.keys(itensTypes).map((key, i) => {
                  return (
                    <option key={i} value={key}>
                      {key} - {itensTypes[key]}
                    </option>
                  );
                })}
              </select>
              {errors.itemType && errors.itemType.type === 'required' && (
                <ErrorMsgField type="required" />
              )}
            </div>
          </div>
          <div className="col-sm-3">
            <div className="form-group">
              <label htmlFor="condition">
                Condição do Produto
                {/* <span className="field-required">*</span> */}
              </label>
              <select
                className="form-control"
                id="condition"
                name="condition"
                value={data.condition}
                onChange={evt => handleChangeDefault(evt, data, setData)}
                ref={register}
              >
                <option value="NEW">Novo</option>
                <option value="USED">Usado</option>
              </select>
              {errors.condition && errors.condition.type === 'required' && (
                <ErrorMsgField type="required" />
              )}
            </div>
          </div>
          <div className="col-sm-3">
            <div className="form-group">
              <label htmlFor="ncm">
                NCM
                {/* <span className="field-required">*</span> */}
              </label>
              <input
                type="text"
                className="form-control"
                id="ncm"
                name="ncm"
                autoComplete="off"
                placeholder="Ex: 96039000"
                number="on"
                value={data.ncm}
                onChange={evt => handleChangeDefault(evt, data, setData)}
                ref={register}
              />
              {errors.ncm && errors.ncm.type === 'required' && (
                <ErrorMsgField type="required" />
              )}
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-sm-3">
            <div className="form-group">
              <label htmlFor="cest">
                CEST
                {/* <span className="field-required">*</span> */}
              </label>
              <input
                type="text"
                className="form-control"
                id="cest"
                name="cest"
                autoComplete="off"
                number="on"
                placeholder="Ex: 2805700"
                value={data.cest}
                onChange={evt => handleChangeDefault(evt, data, setData)}
                ref={register}
              />
              {errors.cest && errors.cest.type === 'required' && (
                <ErrorMsgField type="required" />
              )}
            </div>
          </div>
          <div className="col-sm-2">
            <div className="form-group">
              <label htmlFor="csosn">
                CSOSN
                {/* <span className="field-required">*</span> */}
              </label>
              <input
                type="text"
                className="form-control"
                id="csosn"
                name="csosn"
                autoComplete="off"
                number="on"
                placeholder="Ex: 102"
                value={data.csosn}
                onChange={evt => handleChangeDefault(evt, data, setData)}
                ref={register}
              />
              {errors.csosn && errors.csosn.type === 'required' && (
                <ErrorMsgField type="required" />
              )}
            </div>
          </div>
          <div className="col-sm-2">
            <div className="form-group">
              <label htmlFor="cfopSameState">
                CFOP (Mesmo Estado)
                {/* <span className="field-required">*</span> */}
              </label>
              <input
                type="text"
                className="form-control"
                id="cfopSameState"
                name="cfopSameState"
                autoComplete="off"
                number="on"
                placeholder="Ex: 5102"
                value={data.cfopSameState}
                onChange={evt => handleChangeDefault(evt, data, setData)}
                ref={register}
              />
              {errors.cfopSameState &&
                errors.cfopSameState.type === 'required' && (
                <ErrorMsgField type="required" />
              )}
            </div>
          </div>
          <div className="col-sm-2">
            <div className="form-group">
              <label htmlFor="cfopAnotherState">
                CFOP (Outro Estado)
                {/* <span className="field-required">*</span> */}
              </label>
              <input
                type="text"
                className="form-control"
                id="cfopAnotherState"
                name="cfopAnotherState"
                autoComplete="off"
                number="on"
                placeholder="Ex: 6108"
                value={data.cfopAnotherState}
                onChange={evt => handleChangeDefault(evt, data, setData)}
                ref={register}
              />
              {errors.cfopAnotherState &&
                errors.cfopAnotherState.type === 'required' && (
                <ErrorMsgField type="required" />
              )}
            </div>
          </div>
          <div className="col-sm-3">
            <div className="form-group">
              <label htmlFor="location">Localização Estoque</label>
              <input
                type="text"
                className="form-control"
                id="location"
                name="location"
                autoComplete="off"
                value={data.location}
                onChange={evt => handleChangeDefault(evt, data, setData)}
                ref={register}
              />
              {errors.location && errors.location.type === 'required' && (
                <ErrorMsgField type="required" />
              )}
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-sm-12">
            <div className="form-group">
              <label htmlFor="link">Link Galeria e Arquivos Adicionais</label>
              <div className="form-inline">
                <input
                  type="text"
                  className="form-control w-75 mr-2"
                  id="link"
                  name="link"
                  autoComplete="off"
                  value={data.link}
                  onChange={evt => handleChangeDefault(evt, data, setData)}
                  ref={register}
                />
                <a
                  href={data.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-info w-10"
                >
                  Acessar Arquivos
                </a>
                {errors.link && errors.link.type === 'required' && (
                  <ErrorMsgField type="required" />
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-sm-4">
            <div className="form-group text-center">
              <label htmlFor="additionalInformation">Imagem do Produto</label>
              <img
                className="p-2"
                width="200"
                height="200"
                src={
                  data.image
                    ? URL.createObjectURL(data.image)
                    : `${config.protocol}://${config.ipServerGW}:${config.portServerGW}/api/products/image/507f1f77bcf86cd799439011`
                }
                alt="Imagem"
              />
              <input
                type="file"
                id="image"
                name="image"
                className="form-control-file"
                accept="image/png,image/jpeg"
                onChange={handleChangeImage}
              />
            </div>
          </div>
          <div className="col-sm-8">
            <div className="form-group">
              <label htmlFor="additionalInformation">
                Informações Adicionais
              </label>
              <textarea
                className="form-control"
                id="additionalInformation"
                name="additionalInformation"
                rows="7"
                value={data.additionalInformation}
                onChange={evt => handleChangeDefault(evt, data, setData)}
                ref={register}
              ></textarea>
              {errors.additionalInformation &&
                errors.additionalInformation.type === 'required' && (
                <ErrorMsgField type="required" />
              )}
            </div>
          </div>
        </div>
        <div>
          <button
            type="button"
            className="btn btn-success"
            title="Adicionar Categoria"
            disabled={showModalAddCategory}
            onClick={() => setShowModalAddCategory(true)}
          >
            <i className="fa fa-plus" />
            &nbsp;Categoria
          </button>
        </div>
        <MultiSelect
          avaliableOptions={ctgrs}
          selectedOptions={categoriesOptions.categories}
          handleMultiDeselect={options =>
            handleMultiDeselect(
              options,
              'categories',
              categoriesOptions,
              setCategoriesOptions,
              data,
              setData
            )
          }
          handleMultiSelect={options =>
            handleMultiSelect(
              options,
              'categories',
              categoriesOptions,
              setCategoriesOptions,
              data,
              setData
            )
          }
          valueProp="value"
          textProp="text"
          title="Categorias"
          fieldRequired={true}
          name="categories"
          ref={register}
          error={!!errors.categories}
        />
        {handleModalClose ? (
          <div className="container-fluid p-0">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleModalClose}
            >
              <i className="fa fa-times"></i>
              &nbsp;Fechar
            </button>
            <button
              type="submit"
              ref={submitButton}
              className="btn btn-primary float-right"
            >
              <i className="fa fa-save"></i>
              &nbsp;Salvar
            </button>
          </div>
        ) : (
          <FormFooter url="/products" reference={submitButton} />
        )}
      </form>
    </div>
  );
}

Add.propTypes = {
  handleModalClose: PropTypes.func
};

export default Add;
